export interface IMessageTextPart {
  type: "text" | "link";
  value: string;
}

// The old rich-text chat editor (Quill) wrapped every message in a block tag. It was replaced by
// a plain textarea before the support chat first shipped (2026-04-10), so only a message sent
// before then and starting with such a tag is read as legacy HTML. Other markup is shown as typed.
const LEGACY_EDITOR_START = /^\s*<(p|ol|ul|h[1-6]|blockquote|pre)[\s>]/i;
const LEGACY_EDITOR_SENT_BEFORE = Date.UTC(2026, 3, 11);
const BLOCK_ELEMENTS = "p, div, li, h1, h2, h3, h4, h5, h6, blockquote, pre";
// Their text is code, not part of the message.
const NON_TEXT_ELEMENTS = "script, style, template";
const URL_PATTERN = /https?:\/\/[^\s<>"]+/gi;
const TRAILING_PUNCTUATION = new Set([".", ",", ";", ":", "!", "?", "'", '"', "»", "“", "”"]);
const CLOSING_BRACKETS: Record<string, string> = { ")": "(", "]": "[", "}": "{" };

const countChar = (text: string, char: string) => text.split(char).length - 1;

const isLegacyEditorMessage = (content: string, sentAt: string): boolean =>
  new Date(sentAt).getTime() < LEGACY_EDITOR_SENT_BEFORE && LEGACY_EDITOR_START.test(content);

/**
 * Drops trailing punctuation from a matched link. A closing bracket stays when the link
 * opens it too (https://en.wikipedia.org/wiki/Foo_(bar)). One pass from the end, so a
 * long message cannot slow it down.
 */
const trimLinkEnd = (url: string): string => {
  const unmatchedClosers: Record<string, number> = {};
  for (const [closer, opener] of Object.entries(CLOSING_BRACKETS)) {
    unmatchedClosers[closer] = countChar(url, closer) - countChar(url, opener);
  }

  let end = url.length;
  while (end > 0) {
    const char = url[end - 1];
    if (CLOSING_BRACKETS[char]) {
      if (unmatchedClosers[char] <= 0) break;
      unmatchedClosers[char]--;
    } else if (!TRAILING_PUNCTUATION.has(char)) {
      break;
    }
    end--;
  }

  return url.slice(0, end);
};

/**
 * User messages are plain text and must always be rendered as text, never as HTML.
 * These helpers turn a stored message into text parts the UI renders as React nodes.
 */
export const messageTextUtility = {
  /**
   * Plain text is returned unchanged. Messages saved by the old rich-text editor are
   * read as text, keeping their line breaks. The HTML is parsed in an inert document
   * (DOMParser), so nothing in it loads or runs.
   */
  toPlainText: (content: string, sentAt: string): string => {
    if (!content || !isLegacyEditorMessage(content, sentAt)) return content ?? "";

    const doc = new DOMParser().parseFromString(content, "text/html");
    doc.querySelectorAll(NON_TEXT_ELEMENTS).forEach((element) => element.remove());
    doc.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
    doc.querySelectorAll(BLOCK_ELEMENTS).forEach((block) => block.append("\n"));

    return (doc.body.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
  },

  /** Splits text into plain parts and http(s) links. Trailing punctuation stays text. */
  splitLinks: (text: string): IMessageTextPart[] => {
    const parts: IMessageTextPart[] = [];
    let lastIndex = 0;

    for (const match of text.matchAll(URL_PATTERN)) {
      const start = match.index ?? 0;
      const url = trimLinkEnd(match[0]);
      if (start > lastIndex) parts.push({ type: "text", value: text.slice(lastIndex, start) });
      parts.push({ type: "link", value: url });
      lastIndex = start + url.length;
    }

    if (lastIndex < text.length) parts.push({ type: "text", value: text.slice(lastIndex) });
    return parts;
  },
};
